import React, { useCallback, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import Tooltip from "@material-ui/core/Tooltip";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import { semantic } from "../../theme/tokens";

const useStyles = makeStyles((theme) => ({
  tile: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  tileLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  tileValue: {
    fontSize: "1.65rem",
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
  },
  tileHint: { color: theme.palette.text.secondary },
  panel: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  panelTitle: { marginBottom: theme.spacing(0.5) },
  panelSub: {
    color: theme.palette.text.secondary,
    display: "block",
    marginBottom: theme.spacing(2),
  },
  section: { marginBottom: theme.spacing(3) },
  stageRow: { marginBottom: theme.spacing(2) },
  stageHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: theme.spacing(1),
    marginBottom: 4,
  },
  stageName: { fontWeight: 600, fontSize: 13 },
  stageMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  // Barra dupla: o traço claro é o valor cheio, o escuro é o ponderado.
  // Ver os dois juntos é o ponto — a diferença entre eles é o risco do funil.
  track: {
    position: "relative",
    height: 10,
    borderRadius: 5,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.14)"
        : theme.palette.grey[100],
    overflow: "hidden",
  },
  barGross: { position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 5, opacity: 0.35 },
  barWeighted: { position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 5 },
  monthRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontVariantNumeric: "tabular-nums",
  },
  legend: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  swatch: { width: 14, height: 10, borderRadius: 2, display: "inline-block", marginRight: 6 },
  empty: { padding: theme.spacing(4, 0), textAlign: "center", color: theme.palette.text.secondary },
  filter: { minWidth: 170 },
}));

const money = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[Number(mo) - 1]}/${String(y).slice(2)}`;
};

const StatTile = ({ label, value, hint, color }) => {
  const classes = useStyles();
  return (
    <Paper variant="outlined" className={classes.tile}>
      <div className={classes.tileLabel}>{label}</div>
      <div className={classes.tileValue} style={color ? { color } : undefined}>
        {value}
      </div>
      {hint && (
        <Typography variant="caption" className={classes.tileHint}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
};

const Forecast = () => {
  const classes = useStyles();
  const [start, setStart] = useState(() => isoDate(new Date(new Date().getFullYear(), 0, 1)));
  const [end, setEnd] = useState(() => isoDate(new Date(new Date().getFullYear(), 11, 31)));
  const [pipelineId, setPipelineId] = useState("");
  const [pipelines, setPipelines] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pipelines")
      .then((r) => setPipelines(Array.isArray(r.data) ? r.data : r.data.records || []))
      .catch(() => setPipelines([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get("/analytics/forecast", {
        params: { start, end, pipelineId: pipelineId || undefined },
      });
      setData(result);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [start, end, pipelineId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openStages = (data?.stages || []).filter((s) => s.type === "open" || !s.type);
  const maxValue = Math.max(1, ...openStages.map((s) => s.value));
  const maxMonth = Math.max(1, ...(data?.byMonth || []).map((m) => m.value));
  const confidence =
    data?.openValue > 0 ? Math.round((data.weightedValue / data.openValue) * 100) : null;

  return (
    <MainContainer>
      <PageHeader
        title="Previsão de vendas"
        subtitle="Valor ponderado pela probabilidade de cada etapa do funil. Só negócios em aberto — ganho e perdido são resultado, não previsão."
        filters={
          <>
            <TextField
              select
              label="Pipeline"
              className={classes.filter}
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {pipelines.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Fechados de"
              InputLabelProps={{ shrink: true }}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
            <TextField
              type="date"
              label="até"
              InputLabelProps={{ shrink: true }}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </>
        }
      />

      {loading && <LinearProgress />}

      <div style={{ paddingTop: 16 }}>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Previsão ponderada"
              value={money(data?.weightedValue)}
              color={semantic.success.main}
              hint={
                confidence != null
                  ? `${confidence}% do valor bruto em aberto`
                  : "Sem negócios em aberto"
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Funil bruto"
              value={money(data?.openValue)}
              hint={`${data?.openDeals || 0} negócios em aberto`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Ganho no período"
              value={money(data?.closed?.wonValue)}
              hint={`${data?.closed?.won || 0} negócios fechados`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Perdido no período"
              value={money(data?.closed?.lostValue)}
              color={data?.closed?.lost ? semantic.danger.main : undefined}
              hint={`${data?.closed?.lost || 0} negócios perdidos`}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" className={classes.panel}>
              <Typography variant="h4" className={classes.panelTitle}>
                Por etapa do funil
              </Typography>
              <Typography variant="caption" className={classes.panelSub}>
                A distância entre as duas barras é o risco: quanto do valor
                depende de negócios que ainda estão longe do fechamento.
              </Typography>

              <div className={classes.legend}>
                <Typography variant="caption">
                  <span className={classes.swatch} style={{ backgroundColor: "#256abf", opacity: 0.35 }} />
                  Valor bruto
                </Typography>
                <Typography variant="caption">
                  <span className={classes.swatch} style={{ backgroundColor: "#256abf" }} />
                  Ponderado
                </Typography>
              </div>

              {openStages.some((s) => s.deals > 0) ? (
                openStages.map((s) => (
                  <div key={s.stageId} className={classes.stageRow}>
                    <div className={classes.stageHead}>
                      <span className={classes.stageName}>
                        {s.name}{" "}
                        <Typography component="span" variant="caption" color="textSecondary">
                          {s.probability}%
                        </Typography>
                      </span>
                      <span className={classes.stageMeta}>
                        {s.deals} {s.deals === 1 ? "negócio" : "negócios"} · {money(s.value)} →{" "}
                        <strong>{money(s.weighted)}</strong>
                      </span>
                    </div>
                    <Tooltip title={`${money(s.weighted)} ponderado de ${money(s.value)}`}>
                      <div className={classes.track}>
                        <div
                          className={classes.barGross}
                          style={{ width: `${(s.value / maxValue) * 100}%`, backgroundColor: "#256abf" }}
                        />
                        <div
                          className={classes.barWeighted}
                          style={{ width: `${(s.weighted / maxValue) * 100}%`, backgroundColor: "#256abf" }}
                        />
                      </div>
                    </Tooltip>
                  </div>
                ))
              ) : (
                <div className={classes.empty}>Nenhum negócio em aberto neste pipeline.</div>
              )}

              {data?.withoutCloseDate > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {data.withoutCloseDate}{" "}
                  {data.withoutCloseDate === 1 ? "negócio está" : "negócios estão"} sem
                  data prevista de fechamento e não aparecem na projeção mensal.
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper variant="outlined" className={classes.panel}>
              <Typography variant="h4" className={classes.panelTitle}>
                Projeção mensal
              </Typography>
              <Typography variant="caption" className={classes.panelSub}>
                Distribuída pela data prevista de fechamento de cada negócio.
              </Typography>

              {(data?.byMonth || []).length ? (
                data.byMonth.map((m) => (
                  <div key={m.month}>
                    <div className={classes.monthRow}>
                      <span style={{ fontWeight: 600 }}>{monthLabel(m.month)}</span>
                      <span className={classes.stageMeta}>
                        {m.deals} · {money(m.value)} → <strong>{money(m.weighted)}</strong>
                      </span>
                    </div>
                    <div className={classes.track} style={{ marginTop: 4, marginBottom: 8 }}>
                      <div
                        className={classes.barGross}
                        style={{ width: `${(m.value / maxMonth) * 100}%`, backgroundColor: "#256abf" }}
                      />
                      <div
                        className={classes.barWeighted}
                        style={{ width: `${(m.weighted / maxMonth) * 100}%`, backgroundColor: "#256abf" }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className={classes.empty}>
                  Nenhum negócio em aberto com data prevista de fechamento.
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default Forecast;
